import React, { type FC } from 'react'
/**
 * TODO migrate MuiTextField to common/Input/TextField
 * @see https://cybusio.atlassian.net/browse/CC-4592
 */
import { Autocomplete, Box, Chip, Popper, type SxProps, TextField, type Theme, Typography } from '@mui/material'
import { CloudQueue, DeviceHub, ScatterPlot } from '@mui/icons-material'

import { isArray, type ReadonlyRecord } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, type RuleEngineEndpoint, Translation } from '../../../../domain'

import { CircularLoader } from '../../common'
import { ErrorMessage } from '../../ErrorMessage'
import { FormattedTranslation } from '../../Internationalization'
import { useAppUsecase } from '../../State'
import { useEndpoints, useEndpointsSearch, useSelectedEndpoints } from './Hooks'

/**
 * since the mr: auto should be applied to a typography component (p), we can select everyting thats not a div
 */
const optionStyle: SxProps<Theme> = { display: 'flex', width: '100%', '& > *': { my: 'auto', ml: 1 }, '& > :not(div)': { mr: 'auto' } }
const optionChipStyle: SxProps<Theme> = { pl: 0.5 }

const noSelection = [] as const

export const EndpointsDropdown: FC = () => {
    const filter = useAppUsecase('filterSandboxUsecase')

    const search = useEndpointsSearch()
    const endpoints = useEndpoints()
    const selected = useSelectedEndpoints() ?? noSelection

    return (
        <Autocomplete
            data-testid="rule-engine-endpoints-dropdown"
            multiple
            filterSelectedOptions
            size="medium"
            options={Object.values(
                [...(isArray(endpoints) ? endpoints : []), ...selected]
                    /** Just ignore the duplicates */
                    .reduce<ReadonlyRecord<RuleEngineEndpoint['id'], RuleEngineEndpoint>>((r, option) => ({ ...r, [option.id]: option }), {})
            )}
            value={[...selected]}
            getOptionLabel={(option) => option.id}
            renderOption={(props, option) => (
                <li {...props}>
                    <ScatterPlot color="primary" />
                    <Box sx={optionStyle} title={option.id}>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.service && <Chip sx={optionChipStyle} size="small" variant="outlined" avatar={<CloudQueue />} label={option.service} />}
                        {option.connection && <Chip sx={optionChipStyle} size="small" variant="outlined" avatar={<DeviceHub />} label={option.connection} />}
                    </Box>
                </li>
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        data-testid="selected-endpoint-chip"
                        title={option.id}
                        avatar={<ScatterPlot />}
                        size="small"
                        variant="outlined"
                        label={option.name}
                        {...getTagProps({ index })}
                        key={index}
                    />
                ))
            }
            inputValue={search}
            renderInput={(params) => (
                <TextField {...params} variant="standard" size="medium" onChange={(e) => filter.setEndpointsSearch(e.currentTarget.value)} />
            )}
            noOptionsText={
                <>
                    {ConnectwareError.is(endpoints) &&
                        (ConnectwareError.isOfTypes(endpoints, ConnectwareErrorType.BAD_REQUEST) ? (
                            endpoints.message
                        ) : (
                            <ErrorMessage titleVariant="body2" error={endpoints} />
                        ))}
                    {endpoints === null && <CircularLoader />}
                    {isArray(endpoints) && (
                        <Typography variant="body2">
                            <FormattedTranslation id={Translation.NO_ENDPOINTS_AVAILABLE} />
                        </Typography>
                    )}
                </>
            }
            onChange={(_, endpoints) => filter.selectEndpoints(endpoints)}
            slots={{
                popper: (props) => <Popper data-testid="rule-engine-endpoints-dropdown-popper" {...props} />,
            }}
        />
    )
}
